import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TbTrash } from 'react-icons/tb';
import { MAX_SELECT_OPTION_LENGTH, normalizeSelectOptionsDraft } from '../pages/inquiryTypeShared';

const OPTION_PILL_MIN_TEXT_WIDTH = 28;
const OPTION_PILL_MAX_TEXT_WIDTH = 360;
const OPTION_PILL_TEXT_WIDTH_BUFFER = 14;

function OptionPill({
  value,
  isEditing = false,
  isDraft = false,
  editValue = '',
  disabled = false,
  editLabel = 'Rediger valg',
  inputLabel = 'Nytt valg',
  onBeginEdit,
  onEditChange,
  onCommitEdit,
  onCancelEdit,
  onRemove,
  inputRef,
  testId,
}) {
  const mirrorRef = useRef(null);
  const [textWidth, setTextWidth] = useState(OPTION_PILL_MIN_TEXT_WIDTH);
  const textForMeasure = isEditing || isDraft ? editValue : value;
  const isEditable = isEditing || isDraft;
  const visibleValue = isEditable ? editValue : value;

  useLayoutEffect(() => {
    const mirror = mirrorRef.current;
    if (!mirror) return;
    const measured = Math.ceil(mirror.getBoundingClientRect().width) + OPTION_PILL_TEXT_WIDTH_BUFFER;
    const nextWidth = Math.max(
      OPTION_PILL_MIN_TEXT_WIDTH,
      Math.min(OPTION_PILL_MAX_TEXT_WIDTH, measured),
    );
    setTextWidth(nextWidth);
  }, [textForMeasure]);

  return (
    <span
      className={`inquiry-option-pill${isDraft ? ' inquiry-option-pill--draft' : ''}`}
      style={{ '--option-text-width': `${textWidth}px` }}
      data-testid={testId}
    >
      <span ref={mirrorRef} className="inquiry-option-pill-mirror" aria-hidden="true">
        {textForMeasure || ''}
      </span>
      <input
        ref={inputRef}
        type="text"
        className={`inquiry-option-pill-input${isEditable ? ' is-editing' : ''}`}
        value={visibleValue}
        readOnly={!isEditable}
        onChange={(event) => {
          if (!isEditable) return;
          onEditChange(event.target.value);
        }}
        onClick={() => {
          if (!isEditable && !disabled) {
            onBeginEdit();
          }
        }}
        onFocus={() => {
          if (!isEditable && !disabled) {
            onBeginEdit();
          }
        }}
        onBlur={() => {
          if (isEditable) {
            onCommitEdit();
          }
        }}
        onDoubleClick={() => {
          if (!isEditable && !disabled) {
            onBeginEdit();
          }
        }}
        onKeyDown={(event) => {
          if (!isEditable) {
            if ((event.key === 'Enter' || event.key === ' ') && !disabled) {
              event.preventDefault();
              onBeginEdit();
            }
            return;
          }
          if (event.key === 'Enter') {
            event.preventDefault();
            onCommitEdit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            onCancelEdit();
          }
        }}
        aria-label={isDraft ? inputLabel : isEditing ? editLabel : value}
        disabled={disabled}
      />
      {isDraft ? null : (
        <button
          type="button"
          className="inquiry-option-pill-action inquiry-option-pill-action--danger"
          onMouseDown={(event) => event.preventDefault()}
          onClick={onRemove}
          aria-label={`Fjern ${value}`}
          disabled={disabled}
        >
          <TbTrash aria-hidden="true" focusable="false" />
        </button>
      )}
    </span>
  );
}

export default function SelectOptionsEditor({
  options = [],
  disabled = false,
  onChange,
  addButtonLabel = 'Legg til valg',
  inputLabel = 'Nytt valg',
  editLabel = 'Rediger valg',
}) {
  const normalizedOptions = useMemo(() => normalizeSelectOptionsDraft(options), [options]);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerValue, setComposerValue] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const composerInputRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (editingIndex !== null && editingIndex >= normalizedOptions.length) {
      setEditingIndex(null);
      setEditingValue('');
    }
  }, [editingIndex, normalizedOptions.length]);

  useEffect(() => {
    if (!isComposerOpen || editingIndex !== null) return;
    composerInputRef.current?.focus();
  }, [editingIndex, isComposerOpen]);

  useEffect(() => {
    if (editingIndex === null) return;
    editInputRef.current?.focus();
    editInputRef.current?.select();
  }, [editingIndex]);

  const handleChange = useCallback((nextOptions) => {
    if (typeof onChange === 'function') {
      onChange(normalizeSelectOptionsDraft(nextOptions));
    }
  }, [onChange]);

  const commitComposerValue = useCallback(() => {
    const nextOption = String(composerValue || '').trim().slice(0, MAX_SELECT_OPTION_LENGTH);
    if (!nextOption || normalizedOptions.includes(nextOption)) return false;
    handleChange([...normalizedOptions, nextOption]);
    setComposerValue('');
    setIsComposerOpen(true);
    return true;
  }, [composerValue, handleChange, normalizedOptions]);

  const removeOption = useCallback((indexToRemove) => {
    handleChange(normalizedOptions.filter((_option, index) => index !== indexToRemove));
    if (editingIndex === indexToRemove) {
      setEditingIndex(null);
      setEditingValue('');
    }
  }, [editingIndex, handleChange, normalizedOptions]);

  const beginEditing = useCallback((indexToEdit) => {
    if (disabled) return;
    setEditingIndex(indexToEdit);
    setEditingValue(normalizedOptions[indexToEdit] || '');
    setIsComposerOpen(false);
    setComposerValue('');
  }, [disabled, normalizedOptions]);

  const cancelEditing = useCallback(() => {
    setEditingIndex(null);
    setEditingValue('');
  }, []);

  const commitEditingValue = useCallback(() => {
    if (editingIndex === null) return false;
    const nextOption = String(editingValue || '').trim().slice(0, MAX_SELECT_OPTION_LENGTH);
    if (!nextOption) {
      cancelEditing();
      return false;
    }
    if (normalizedOptions.some((option, index) => index !== editingIndex && option === nextOption)) {
      return false;
    }
    handleChange(
      normalizedOptions.map((option, index) => (index === editingIndex ? nextOption : option)),
    );
    setEditingIndex(null);
    setEditingValue('');
    return true;
  }, [cancelEditing, editingIndex, editingValue, handleChange, normalizedOptions]);

  const handleAddButtonClick = useCallback(() => {
    if (disabled || editingIndex !== null) return;
    if (!isComposerOpen) {
      setIsComposerOpen(true);
      return;
    }
    if (!commitComposerValue()) {
      composerInputRef.current?.focus();
    }
  }, [commitComposerValue, disabled, editingIndex, isComposerOpen]);

  return (
    <div
      className={`inquiry-option-editor${disabled ? ' is-disabled' : ''}`}
      role="group"
      aria-label="Valg"
    >
      {normalizedOptions.map((option, index) => (
        <OptionPill
          key={`${option}-${index}`}
          value={option}
          isEditing={editingIndex === index}
          editValue={editingValue}
          disabled={disabled}
          editLabel={editLabel}
          onBeginEdit={() => beginEditing(index)}
          onEditChange={setEditingValue}
          onCommitEdit={commitEditingValue}
          onCancelEdit={cancelEditing}
          onRemove={() => removeOption(index)}
          inputRef={editingIndex === index ? editInputRef : null}
          testId={`option-pill-${index}`}
        />
      ))}

      {isComposerOpen ? (
        <OptionPill
          value=""
          isDraft
          editValue={composerValue}
          disabled={disabled}
          inputLabel={inputLabel}
          onEditChange={setComposerValue}
          onCommitEdit={commitComposerValue}
          onCancelEdit={() => {
            if (!composerValue.trim()) {
              setIsComposerOpen(false);
            }
          }}
          inputRef={composerInputRef}
          testId="option-pill-draft"
          onRemove={() => {}}
        />
      ) : null}

      <button
        type="button"
        className="inquiry-option-editor-add"
        onClick={handleAddButtonClick}
        aria-label={addButtonLabel}
        disabled={disabled || editingIndex !== null}
      >
        +
      </button>
    </div>
  );
}
